import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import React, {
  useState,
  useRef,
  DragEvent,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { toast } from "react-hot-toast";
import { Document, Page, pdfjs } from "react-pdf";
import {
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import Input from "../common/Input";
import TextArea from "../Settings/TextArea";
import { FormTemplate } from "../../db/schema";
import { parsePdfFormFields, ParsedPdfResult } from "../../services/pdfService";
import { startFormUpload } from "../../services/formUploadService";
import ReferencePdfPicker from "./ReferencePdfPicker";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

interface FormTemplateUploadProps {
  modalRef: React.RefObject<HTMLDialogElement>;
  agencyOnly?: boolean;
  getUploadUrl?: (
    fileName: string,
    fileType: string
  ) => Promise<{ uploadURL: string; key: string; url: string }>;
  createTemplate?: (data: any) => Promise<FormTemplate>;
  // Where the created template lives (and its /auto-fields job). Defaults to
  // the officer's personal templates; the agency manager passes its own.
  templatesApiBase?: string;
  // Where the designer lives, for the upload tray's link. Defaults to personal.
  getOpenPath?: (templateId: string) => string;
  // A PDF dropped on the page behind the modal; the page opens the dialog and
  // hands it over here. A new File object is one drop.
  droppedFile?: File | null;
}

const MAX_PDF_SIZE_MB = 50; // 50MB max for PDFs

const FormTemplateUpload: React.FC<FormTemplateUploadProps> = ({
  modalRef,
  agencyOnly = false,
  getUploadUrl,
  createTemplate,
  templatesApiBase = "/api/forms/templates",
  getOpenPath = (id) => `/forms/${id}/edit`,
  droppedFile,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  // Optional filled-in copy of the form for the AI to compare against.
  const [referenceFile, setReferenceFile] = useState<File | null>(null);

  // Parsed PDF result
  const [parsedResult, setParsedResult] = useState<ParsedPdfResult | null>(null);

  // Form fields
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [scope, setScope] = useState<"personal" | "agency">(
    agencyOnly ? "agency" : "personal"
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : null),
    [selectedFile]
  );

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const busy = isParsing;

  const resetModal = useCallback(() => {
    setSelectedFile(null);
    setReferenceFile(null);
    setIsDragOver(false);
    setIsParsing(false);
    setPreviewPage(1);
    setParsedResult(null);
    setTemplateName("");
    setTemplateDescription("");
    setScope(agencyOnly ? "agency" : "personal");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [agencyOnly]);

  // Clear staged state whenever the dialog is dismissed, so reopening never
  // shows the previous file (mirrors UploadModal).
  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;
    const observer = new MutationObserver(() => {
      if (!dialog.hasAttribute("open")) {
        resetModal();
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
    return () => observer.disconnect();
  }, [modalRef, resetModal]);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = async (file: File) => {
    // Validate file type
    if (file.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return;
    }

    // Validate file size
    if (file.size > MAX_PDF_SIZE_MB * 1024 * 1024) {
      toast.error(`File size must be less than ${MAX_PDF_SIZE_MB}MB`);
      return;
    }

    setSelectedFile(file);
    setParsedResult(null);
    setPreviewPage(1);

    // Auto-populate template name from filename
    if (!templateName) {
      const nameWithoutExt = file.name.replace(/\.pdf$/i, "");
      setTemplateName(nameWithoutExt);
    }

    // Parse PDF to detect form fields
    setIsParsing(true);
    try {
      const result = await parsePdfFormFields(file);
      setParsedResult(result);

      if (result.isFillable) {
        toast.success(`Detected ${result.fields.length} form fields`);
      }
    } catch (error) {
      console.error("Error parsing PDF:", error);
      toast.error("Could not analyze PDF structure");
      setParsedResult({ isFillable: false, pageCount: 0, fields: [] });
    } finally {
      setIsParsing(false);
    }
  };

  // A PDF dropped on the page behind the modal goes through the same checks
  // and field detection as one chosen in the dialog.
  useEffect(() => {
    if (droppedFile) void handleFileSelect(droppedFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFile]);

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setParsedResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Hand the file to the upload store and close: the tray shows progress and
  // the AI step, and keeps going if the officer navigates away.
  const handleUpload = () => {
    if (!selectedFile) {
      toast.error("Please select a PDF file");
      return;
    }

    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    startFormUpload({
      file: selectedFile,
      name: templateName.trim(),
      description: templateDescription.trim() || undefined,
      scope,
      isFillable: parsedResult?.isFillable || false,
      fields: parsedResult?.fields || [],
      referenceFile: referenceFile ?? undefined,
      getUploadUrl,
      createTemplate,
      templatesApiBase,
      openPath: getOpenPath,
    });
    modalRef.current?.close();
    resetModal();
  };

  const handleClose = () => {
    if (
      selectedFile &&
      !window.confirm(
        "You have an unsaved template. Are you sure you want to leave?"
      )
    ) {
      return;
    }
    modalRef.current?.close();
  };

  // Block browser navigation (tab close, refresh) while a file is staged.
  useEffect(() => {
    if (!selectedFile) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [selectedFile]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Group fields by type for preview
  const fieldsByType = parsedResult?.fields.reduce((acc, field) => {
    acc[field.type] = (acc[field.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Upload Form Template</h3>
          <button onClick={handleClose}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* File Upload Area */}
        <div
          data-tour="form-upload-drop"
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors hover:bg-gray-100 dark:hover:bg-base-200 cursor-pointer ${
            isDragOver
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40"
              : "border-gray-300 dark:border-base-300"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileInputChange}
            className="hidden"
          />
          <CloudArrowUpIcon className="size-12 mx-auto text-blue-400" />
          <p className="text-gray-800 dark:text-base-content/80 text-md font-medium max-w-96 mx-auto">
            Choose a file or drag and drop
          </p>
          <p className="text-base-content/60 text-sm max-w-96 mx-auto">
            PDF — under {MAX_PDF_SIZE_MB}MB
          </p>
        </div>

        {/* Parsing indicator */}
        {isParsing && (
          <div className="mt-4 flex items-center gap-3">
            <span className="loading loading-spinner loading-md text-primary"></span>
            <div>
              <p className="font-medium">Analyzing PDF...</p>
              <p className="text-sm text-base-content/60">
                Detecting form fields and structure
              </p>
            </div>
          </div>
        )}

        {/* Selected file */}
        {selectedFile && !isParsing && (
          <div className="mt-4 border border-base-300 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-3 bg-base-200">
              <div className="flex items-center gap-2 min-w-0">
                <DocumentIcon className="h-5 w-5 shrink-0 text-blue-400" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{selectedFile.name}</p>
                  <p className="text-xs text-base-content/60">
                    {formatFileSize(selectedFile.size)}
                    {parsedResult &&
                      parsedResult.pageCount > 0 &&
                      ` • ${parsedResult.pageCount} page${
                        parsedResult.pageCount === 1 ? "" : "s"
                      }`}
                  </p>
                </div>
              </div>
              <button
                onClick={handleRemoveFile}
                disabled={busy}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Remove file"
              >
                <TrashIcon className="h-5 w-5 text-red-500" />
              </button>
            </div>

            <div className="p-4 space-y-3 bg-base-100 border-t border-base-300">
              {/* Detected fields summary */}
              {parsedResult && (
                <div
                  data-tour="form-upload-detected"
                  className={cn(
                    "rounded-lg border p-3 text-xs",
                    parsedResult.isFillable
                      ? "bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800"
                      : "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800"
                  )}
                >
                  {parsedResult.isFillable ? (
                    <div className="flex items-start gap-2">
                      <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
                      <div className="min-w-0">
                        <p className="font-medium text-green-800 dark:text-green-300">
                          Detected {parsedResult.fields.length} form field
                          {parsedResult.fields.length === 1 ? "" : "s"}
                        </p>
                        <p className="text-green-700 dark:text-green-400 mt-0.5">
                          These will be extracted automatically. You can adjust
                          them in the designer.
                        </p>
                        {fieldsByType && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {Object.entries(fieldsByType).map(
                              ([type, count]) => (
                                <span
                                  key={type}
                                  className="badge badge-outline badge-sm"
                                >
                                  {count} {type}
                                </span>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                      <div>
                        <p className="font-medium text-amber-800 dark:text-amber-400">
                          No form fields detected
                        </p>
                        <p className="text-amber-700 dark:text-amber-400/80 mt-0.5">
                          This is a flat PDF — we'll read it and place the fields
                          for you.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Input
                title="Template Name *"
                data-tour="form-upload-name"
                value={templateName}
                onChange={(e) =>
                  setTemplateName((e.target as HTMLInputElement).value)
                }
                placeholder="e.g., Use of Force Report"
                className="w-full !max-w-full bg-base-100 !mt-0"
              />

              <TextArea
                label="Description (optional)"
                data-tour="form-upload-description"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Brief description of this form template"
                rows={2}
                labelSize="sm"
                labelClassName="!min-h-7 mb-1"
              />

              {!agencyOnly && (
                <div data-tour="form-upload-scope">
                  <p className="font-medium text-sm pb-1 m-0">Template Scope</p>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      onClick={() => setScope("personal")}
                      variant={scope === "personal" ? "secondary" : "outline"}
                      className={cn(
                        "!border h-auto text-xs",
                        scope === "personal"
                          ? "border-blue-500"
                          : "text-base-content/60 bg-base-100",
                        "px-2.5 py-1 rounded-md"
                      )}
                      size="sm"
                    >
                      Personal (only you)
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setScope("agency")}
                      variant={scope === "agency" ? "secondary" : "outline"}
                      className={cn(
                        "!border h-auto text-xs",
                        scope === "agency"
                          ? "border-blue-500"
                          : "text-base-content/60 bg-base-100",
                        "px-2.5 py-1 rounded-md"
                      )}
                      size="sm"
                    >
                      Agency (shared)
                    </Button>
                  </div>
                </div>
              )}

              <ReferencePdfPicker
                file={referenceFile}
                onChange={setReferenceFile}
                disabled={busy}
              />

              {/* PDF Preview */}
              {objectUrl && (
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-base-content/80 mb-2">
                    Preview
                  </p>
                  <div className="flex justify-center bg-base-200 rounded-lg p-3 max-h-[320px] overflow-auto">
                    <Document
                      file={objectUrl}
                      loading={
                        <div className="flex items-center justify-center h-48">
                          <span className="loading loading-spinner loading-md text-primary"></span>
                        </div>
                      }
                      error={
                        <div className="text-error text-sm">
                          Failed to load PDF preview
                        </div>
                      }
                    >
                      <Page
                        pageNumber={previewPage}
                        width={260}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        loading={
                          <div
                            className="flex items-center justify-center"
                            style={{ width: 260, height: 336 }}
                          >
                            <span className="loading loading-spinner loading-md text-primary"></span>
                          </div>
                        }
                      />
                    </Document>
                  </div>
                  {parsedResult && parsedResult.pageCount > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-2">
                      <button
                        onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                        disabled={previewPage <= 1}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeftIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
                      </button>
                      <span className="text-sm text-base-content/60">
                        Page {previewPage} of {parsedResult.pageCount}
                      </span>
                      <button
                        onClick={() =>
                          setPreviewPage((p) =>
                            Math.min(parsedResult.pageCount, p + 1)
                          )
                        }
                        disabled={previewPage >= parsedResult.pageCount}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRightIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="modal-action">
          <Button
            data-tour="form-upload-submit"
            onClick={handleUpload}
            disabled={
              !selectedFile || !templateName.trim() || busy || !parsedResult
            }
            variant="primary"
            className="w-full"
          >
            {parsedResult?.isFillable
              ? `Upload with ${parsedResult.fields.length} Fields`
              : "Upload & Auto-detect Fields"}
          </Button>
        </div>
      </div>
    </dialog>
  );
};

export default FormTemplateUpload;
